import { FC } from 'react';
import { Info } from 'lucide-react';
import {
  useClaimUserProjectMutation,
  useDisclaimUserProjectMutation,
  type UserProject,
} from '@teachur/http-client';
import { Button } from '@new/common/ui/Button/Button';
import { Tooltip } from '@new/common/ui/Tooltip/Tooltip';
import { formatFullName } from '../../utils/projectReviewFormatters';
import { toast } from 'sonner';

interface ButtonWithTooltipProps {
  label: string;
  tooltipContent: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}

const ButtonWithTooltip: FC<ButtonWithTooltipProps> = ({
  label,
  tooltipContent,
  onClick,
  disabled,
  className,
}) => (
  <div className="flex items-center gap-3">
    <Button variant="ghost" onClick={onClick} disabled={disabled} className={className}>
      {label}
    </Button>
    <Tooltip content={tooltipContent}>
      <Info className="w-5 h-5 text-(--primary-O300)! hover:text-(--neutral-white-NW1000)! transition-colors" />
    </Tooltip>
  </div>
);

interface ProjectClaimActionsProps {
  project: UserProject;
  currentUserId?: number;
  showClaim: boolean;
  showDisclaim: boolean;
  showAdmin: boolean;
}

export const ProjectClaimActions: FC<ProjectClaimActionsProps> = ({
  project,
  currentUserId,
  showClaim,
  showDisclaim,
  showAdmin,
}) => {
  const { mutate: claim, isPending: isClaiming } = useClaimUserProjectMutation();
  const { mutate: disclaim, isPending: isDisclaiming } = useDisclaimUserProjectMutation();

  const canShowClaimActions = showClaim || showDisclaim || showAdmin;

  if (!canShowClaimActions) return null;

  const claimedByName = formatFullName(
    project.instructor?.firstName,
    project.instructor?.lastName,
    'another professor'
  );

  const onClaim = () => {
    if (!currentUserId) return;

    claim(
      {
        id: project.id,
        instructorId: currentUserId,
      },
      {
        onSuccess: () => {
          toast.success(
            'Thank you for claiming this project. Please complete the review within 7 days from when the project was submitted.'
          );
        },
      }
    );
  };

  const onDisclaim = () => {
    disclaim(project.id);
  };

  const onAdminUnclaim = () => {
    disclaim(project.id, {
      onSuccess: () => {
        toast.success('Project unclaimed. It is now open for another professor to claim.');
      },
    });
  };

  return (
    <>
      {showClaim && (
        <Button
          variant="secondary"
          onClick={onClaim}
          disabled={isClaiming || isDisclaiming}
          className="w-36.5! border-(--neutral-white-NW1000)! bg-(--neutral-white-NW1000)! text-(--primary-O1000)!"
        >
          Claim project
        </Button>
      )}

      {showDisclaim && (
        <ButtonWithTooltip
          label="Disclaim project"
          onClick={onDisclaim}
          disabled={isClaiming || isDisclaiming}
          tooltipContent="Please complete the review within 7 days from when the project was submitted."
          className="w-36.5! border! border-(--primary-O600)! bg-linear-to-r from-(--primary-O800) to-(--primary-O900)"
        />
      )}

      {showAdmin && (
        <ButtonWithTooltip
          label="Unclaim project"
          onClick={onAdminUnclaim}
          disabled={isDisclaiming}
          tooltipContent={`Currently claimed by ${claimedByName}. Unclaiming will open this project for any professor to claim.`}
          className="h-9! w-auto! cursor-pointer rounded-md! border! border-(--primary-O600)! bg-linear-to-r from-(--primary-O800) to-(--primary-O900) px-3! py-3!"
        />
      )}
    </>
  );
};
